/**
 * ContractorContactsViewElement — blok „Kontakty kontrahenta" jako przenośny
 * klocek kreatora layoutu (konfigurowalne zakładki, kartoteka Kontrahenci).
 *
 * W builderze (mode='builder') renderuje się jako PEŁNOWYMIAROWA formatka listy:
 * pokazuje wszystkie KOLUMNY listy kontaktów (Nazwa/Stanowisko/Email/Telefon/Kategoria),
 * każdą z inline ikonką oka — klik chowa/pokazuje kolumnę na liście kontaktów na karcie
 * (zapis do `settings.displayFields` = allowlista slugów pól, którą czyta `ContactsTab`).
 * `fullWidth: true` → col-span-full w gridzie sekcji.
 *
 * Na REALNEJ stronie kartoteki dispatchowany przez
 * `contractorContextRenderers['contractor_contacts_view']` (→ `ContactsTab`), który
 * respektuje `settings.displayFields`. `allowedModules: ['contractors']`.
 */

import { Users } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import i18n from '@/i18n';
import { applyOrder, parseSlugList, reorderSlugs } from '@/lib/kartoteki/formatka-order';

import { FormatkaReorderList } from './FormatkaReorderList';

/**
 * Kolumny listy kontaktów (stałe, mirror `ContactsTab`): każda kolumna mapuje na
 * jeden lub więcej slugów pola modułu `contractor_contacts` w `displayFields`.
 * `first_name`+`last_name` → „Nazwa"; `phone`+`mobile` → „Telefon".
 */
const CONTACT_COLUMNS: ReadonlyArray<{ key: string; labelKey: string; slugs: string[] }> = [
  {
    key: 'name',
    labelKey: 'tenant.contractors.contacts.columns.name',
    slugs: ['first_name', 'last_name'],
  },
  {
    key: 'position',
    labelKey: 'tenant.contractors.contacts.columns.position',
    slugs: ['position'],
  },
  { key: 'email', labelKey: 'tenant.contractors.contacts.columns.email', slugs: ['email'] },
  {
    key: 'phone',
    labelKey: 'tenant.contractors.contacts.columns.phone',
    slugs: ['phone', 'mobile'],
  },
  {
    key: 'category',
    labelKey: 'tenant.contractors.contacts.columns.category',
    slugs: ['category_id'],
  },
];

const COLUMN_BY_KEY = new Map(CONTACT_COLUMNS.map((c) => [c.key, c]));
const ALL_KEYS = CONTACT_COLUMNS.map((c) => c.key);

/** Zbiór kluczy kolumn WIDOCZNYCH wg `settings.displayFields` (undefined/pusty = wszystkie). */
function visibleColumnKeys(settings: Record<string, unknown> | undefined): Set<string> {
  const raw = settings?.displayFields;
  const df = Array.isArray(raw) ? raw.filter((s): s is string => typeof s === 'string') : null;
  if (!df || df.length === 0) return new Set(ALL_KEYS);
  return new Set(
    CONTACT_COLUMNS.filter((c) => c.slugs.some((s) => df.includes(s))).map((c) => c.key),
  );
}

export const ContractorContactsViewElement: LayoutElementDefinition = {
  type: 'contractor_contacts_view',
  category: 'tresc',
  isContainer: false,
  fullWidth: true,
  label: 'Kontakty kontrahenta',
  icon: 'users',
  allowedModules: ['contractors'],
  defaultSettings: () => ({}),
  render: ({ element, mode, onSettingsChange, systemHideOnly }) => {
    const visible = visibleColumnKeys(element.settings);
    // `hidden` (dla oka) = klucze NIE-widoczne (kontakty używają allowlisty displayFields).
    const hiddenSlugs = new Set(ALL_KEYS.filter((k) => !visible.has(k)));
    const orderedKeys = applyOrder(ALL_KEYS, parseSlugList(element.settings, 'columnOrder'));
    const canHide = mode === 'builder' && !!onSettingsChange;
    // Systemowy layout: tylko ukrywanie (bez reorderu), oko przebija inertny canvas.
    const canReorder = canHide && !systemHideOnly;

    const items = orderedKeys.map((key) => {
      const col = COLUMN_BY_KEY.get(key);
      return {
        slug: key,
        label: col ? i18n.t(col.labelKey) : key,
        hint: i18n.t('tenant.contractors.contactsBlock.columnKind', { defaultValue: 'kolumna' }),
      };
    });

    return (
      <FormatkaReorderList
        elementId={element.id}
        items={items}
        hiddenSlugs={hiddenSlugs}
        canHide={canHide}
        canReorder={canReorder}
        pierceLock={!!systemHideOnly}
        onReorder={(active, over) =>
          onSettingsChange?.({ columnOrder: reorderSlugs(orderedKeys, active, over) })
        }
        onToggleHidden={(key) => {
          const nextVisible = new Set(visible);
          if (nextVisible.has(key)) nextVisible.delete(key);
          else nextVisible.add(key);
          // Zapisujemy jawną allowlistę slugów widocznych kolumn (ContactsTab ją czyta).
          const displayFields = CONTACT_COLUMNS.filter((c) => nextVisible.has(c.key)).flatMap(
            (c) => c.slugs,
          );
          onSettingsChange?.({ displayFields });
        }}
        header={
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
            <Users className="h-4 w-4 text-text-muted" aria-hidden={true} />
            {i18n.t('tenant.contractors.contactsBlock.paletteLabel', {
              defaultValue: 'Kontakty kontrahenta',
            })}
            <span className="text-text-muted">
              {' · '}
              {i18n.t('tenant.contractors.contactsBlock.columnsHint', {
                defaultValue: 'kolumny listy',
              })}
            </span>
          </p>
        }
        hiddenLabel={i18n.t('tenant.contractors.contactsBlock.fieldHidden', {
          defaultValue: 'ukryte',
        })}
        labels={{
          hide: i18n.t('tenant.contractors.contactsBlock.hideField', {
            defaultValue: 'Ukryj kolumnę',
          }),
          show: i18n.t('tenant.contractors.contactsBlock.showField', {
            defaultValue: 'Pokaż kolumnę',
          }),
          drag: i18n.t('tenant.formatkaReorder.dragHint', {
            defaultValue: 'Przeciągnij, aby zmienić kolejność',
          }),
        }}
        testId={`contractor-contacts-view-element-${element.id}`}
        rowTestIdPrefix="contractor-contacts-column"
        elementType="contractor_contacts_view"
      />
    );
  },
};
